import { useMutation, useQuery } from '@apollo/client'
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    Divider,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { OutreachStatus, SystemStatus } from '__generated__/graphql'
import FileUploadButton from 'candidate/components/FileUploadButton'
import config from 'config'
import isEqual from 'lodash.isequal'
import startCase from 'lodash.startcase'
import { createRef, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { parsePhoneNumber } from 'react-phone-number-input'
import {
    GetScreeningInterviewTeams,
    UpdateCandidateProfile,
} from 'recruiter/graphql/candidateProfile.gql'
import DrawerCancelSave from 'shared/components/DrawerCancelSave'
import DrawerComponent from 'shared/components/DrawerComponent'
import NonFormikPhoneInput from 'shared/components/NonFormikPhoneInput'
import useToast from 'shared/components/Toast/useToast'
import { CandidateRecruitingStatuses } from 'shared/models/candidateRecruitingStatus'
import { getOutreachStatusLabel, getPxAsRem } from 'shared/utils'
import { isValidLinkedInUrl } from 'shared/utils/validation'
import * as Yup from 'yup'
import { DialogButtonContainer, StyledDialogContent, Subtitle } from './styled'

import type {
    GetCandidateProfileForRecruiterQuery,
    GetCandidateProfileForRecruiterQueryVariables,
} from '__generated__/graphql'
import type { ApolloQueryRefetchFunction } from 'shared/types/graphql'

interface EditProfileProps {
    open: boolean
    onClose: () => void
    data: GetCandidateProfileForRecruiterQuery
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
}

const EditProfile: FC<EditProfileProps> = ({
    open,
    onClose,
    data,
    refetch,
}) => {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [image, setImage] = useState('')
    const [imageFile, setImageFile] = useState<File>()
    const inputRef = createRef<HTMLInputElement>()
    const toast = useToast()
    const [imageLoading, setImageLoading] = useState(false)
    const [profileImage, setProfileImage] = useState(
        data.candidate?.user?.profileImageKey ||
            data.candidate?.internalProfileImageKey
    )
    const [formValues, setFormValues] = useState({
        firstName: data.candidate?.user?.firstName ?? '',
        lastName: data.candidate?.user?.lastName ?? '',
        nickname: data.candidate?.user?.nickname ?? '',
        email: data.candidate?.user?.email ?? '',
        phone: data.candidate?.user?.phone ?? '',
        linkedinUrl: data.candidate?.linkedinUrl ?? '',
        prefersWorkEmail: data.candidate?.prefersWorkEmail ?? false,
        recruitingStatus: data.candidate?.recruitingStatus?.status ?? '',
        outreachStatus:
            data.candidate?.outreachStatus?.status ?? OutreachStatus.NoStatus,
        systemStatus:
            data.candidate?.systemStatus?.status ?? SystemStatus.NoStatus,
        nonPoachDate: data.candidate?.outreachStatus?.nonPoachDate ?? null,
        screeningInterviewTeam:
            data.candidate?.interviewBucket?.screeningInterviewTeam,
        workEmail: data.candidate?.currentWorkExperience?.workEmail,
    })

    const {
        data: screeningInterviewTeams,
        loading: screeningInterviewTeamsLoading,
        error: screeningInterviewTeamsError,
    } = useQuery(GetScreeningInterviewTeams)
    const canSelectScreeningInterviewTeam =
        screeningInterviewTeams?.screeningInterviewTeams &&
        formValues.systemStatus === SystemStatus.SentScheduler &&
        !screeningInterviewTeamsLoading &&
        !screeningInterviewTeamsError

    const [errorValues, setErrorValues] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        linkedinUrl: '',
        sentSchedulerScreeningInterviewTeamSelection: '',
    })

    const unsavedChangesExist = useMemo(() => {
        const apiData = {
            firstName: data.candidate?.user?.firstName ?? '',
            lastName: data.candidate?.user?.lastName ?? '',
            nickname: data.candidate?.user?.nickname ?? '',
            email: data.candidate?.user?.email ?? '',
            phone: data.candidate?.user?.phone ?? '',
            linkedinUrl: data.candidate?.linkedinUrl ?? '',
            prefersWorkEmail: data.candidate?.prefersWorkEmail ?? false,
            recruitingStatus: data.candidate?.recruitingStatus?.status ?? '',
            outreachStatus:
                data.candidate?.outreachStatus?.status ??
                OutreachStatus.NoStatus,
            systemStatus:
                data.candidate?.systemStatus?.status ?? SystemStatus.NoStatus,
            nonPoachDate: data.candidate?.outreachStatus?.nonPoachDate ?? null,
            screeningInterviewTeam:
                data.candidate?.interviewBucket?.screeningInterviewTeam,
        }
        if (!isEqual(formValues, apiData)) return true
        return false
    }, [formValues, data])

    const resetForm = useCallback(() => {
        setFormValues({
            firstName: data.candidate?.user?.firstName ?? '',
            lastName: data.candidate?.user?.lastName ?? '',
            nickname: data.candidate?.user?.nickname ?? '',
            email: data.candidate?.user?.email ?? '',
            phone: data.candidate?.user?.phone ?? '',
            linkedinUrl: data.candidate?.linkedinUrl ?? '',
            prefersWorkEmail: data.candidate?.prefersWorkEmail ?? false,
            recruitingStatus: data.candidate?.recruitingStatus?.status ?? '',
            outreachStatus:
                data.candidate?.outreachStatus?.status ??
                OutreachStatus.NoStatus,
            systemStatus:
                data.candidate?.systemStatus?.status ?? SystemStatus.NoStatus,
            nonPoachDate: data.candidate?.outreachStatus?.nonPoachDate ?? null,
            screeningInterviewTeam:
                data.candidate?.interviewBucket?.screeningInterviewTeam,
            workEmail: data.candidate?.currentWorkExperience?.workEmail,
        })
        setErrorValues({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            linkedinUrl: '',
            sentSchedulerScreeningInterviewTeamSelection: '',
        })
    }, [data])

    const handleCancel = () => {
        resetForm()
        onClose()
    }

    const [updateBio] = useMutation(UpdateCandidateProfile, {
        onError: (error) => {
            console.error(error)
            toast({
                title: 'Error!',
                message: 'Something went wrong. Please try again later.',
                severity: 'error',
            })
        },
        onCompleted: (data) => {
            if (data.updateCandidateProfile?.success) {
                toast({
                    title: 'Success!',
                    message: 'Profile updated successfully',
                    severity: 'success',
                })
            } else {
                console.log(data.updateCandidateProfile.message)
                toast({
                    title: 'Error!',
                    message:
                        data.updateCandidateProfile.message ??
                        'Something went wrong. Please try again later.',
                    severity: 'error',
                })
            }
            onClose()
            refetch()
        },
    })

    const handleSave = async () => {
        await updateBio({
            variables: {
                ...formValues,
                id: data.candidate?.user?.id ?? '',
            },
        })
    }

    // profile pic handlers
    const handleSelectFile = (e) => {
        const file = e.target.files[0]
        if (file) {
            setImageFile(file)
            setImage(URL.createObjectURL(file))
            setDialogOpen(true)
        }
    }

    const handleUpload = () => {
        if (imageFile) {
            setImageLoading(true)
            saveImage(imageFile)
        }
    }

    const closeDialog = () => {
        setDialogOpen(false)
        setImageLoading(false)
        if (inputRef.current) inputRef.current.value = ''
    }

    async function saveImage(image: File): Promise<void> {
        const formData = new FormData()
        formData.append('picture', image)
        formData.append('candidateId', data.candidate?.user?.id || '')
        // TODO: handle updating internal profile image key instead
        try {
            const response = await fetch(`${config.api.uri}/profilePicture`, {
                method: 'POST',
                body: formData,
                headers: {
                    accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            })
            const data = await response.json()
            if (response.status === 200 && data?.signedUrl) {
                setProfileImage(data.signedUrl)
                refetch()
                toast({
                    title: 'Profile image updated',
                    message: '',
                    severity: 'success',
                })
            } else {
                console.error('image upload error: ', data)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('image upload error: ', error)
            // TODO: more detailed error message?
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        } finally {
            closeDialog()
        }
    }

    useEffect(() => {
        if (!open) resetForm()
    }, [open, resetForm])

    return (
        <DrawerComponent
            open={open}
            onClose={() => {
                resetForm()
                onClose()
            }}
            headerTitle="Edit Profile"
            hasSaveButton={false}
            width="40vw"
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: getPxAsRem(16),
                    margin: getPxAsRem(16, 0, 24),
                }}
            >
                <Avatar src={profileImage ?? ''} />

                <FileUploadButton
                    label="change"
                    ref={inputRef}
                    onChange={handleSelectFile}
                    disabled={!!data?.candidate?.user?.profileImageKey}
                />
            </Box>
            <Stack direction="column" gap={3}>
                <Stack gap={2}>
                    <Subtitle variant="subtitle1">Profile Status</Subtitle>
                    <Stack gap={3}>
                        <Autocomplete
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Recruiting Status"
                                    placeholder="-"
                                />
                            )}
                            onChange={(event, newValue) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    recruitingStatus: newValue || '',
                                }))
                            }}
                            value={formValues.recruitingStatus}
                            options={CandidateRecruitingStatuses}
                        />
                        <Autocomplete
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Off Limits Status"
                                    placeholder="-"
                                />
                            )}
                            onChange={(event, newValue) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    outreachStatus:
                                        newValue || OutreachStatus.NoStatus,
                                }))
                                if (newValue !== OutreachStatus['Non-Poach']) {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        nonPoachDate: null,
                                    }))
                                }
                            }}
                            value={formValues.outreachStatus ?? ''}
                            options={Object.values(OutreachStatus)}
                            getOptionLabel={(option) =>
                                getOutreachStatusLabel(option)
                            }
                        />
                        {formValues.outreachStatus ===
                            OutreachStatus['Non-Poach'] && (
                            <DatePicker
                                value={
                                    formValues.nonPoachDate
                                        ? new Date(formValues.nonPoachDate)
                                        : undefined
                                }
                                onChange={(newValue) =>
                                    setFormValues((prev) => ({
                                        ...prev,
                                        nonPoachDate:
                                            newValue?.toISOString() || '',
                                    }))
                                }
                                label={'Non-Poach Date'}
                            />
                        )}
                        <Autocomplete
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Registration Status"
                                    placeholder="-"
                                />
                            )}
                            onChange={(event, newValue) => {
                                const isSentScheduler =
                                    newValue === SystemStatus.SentScheduler
                                const screeningInterviewTeam = isSentScheduler
                                    ? formValues.screeningInterviewTeam
                                    : null

                                setFormValues((prev) => ({
                                    ...prev,
                                    systemStatus:
                                        newValue || SystemStatus.NoStatus,
                                    screeningInterviewTeam,
                                }))

                                setErrorValues((prev) => ({
                                    ...prev,
                                    sentSchedulerScreeningInterviewTeamSelection:
                                        isSentScheduler &&
                                        !screeningInterviewTeam
                                            ? 'Screening interview team selection is required'
                                            : '',
                                }))
                            }}
                            value={formValues.systemStatus}
                            options={Object.values(SystemStatus).filter(
                                (status) =>
                                    status !== SystemStatus.NoStatus &&
                                    status !== SystemStatus.Null
                            )}
                            getOptionLabel={(option) => startCase(option)}
                        />

                        {canSelectScreeningInterviewTeam && (
                            <Autocomplete
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Screening Interview Team"
                                        placeholder="-"
                                        required
                                    />
                                )}
                                onChange={(e, newValue) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        screeningInterviewTeam:
                                            newValue || null,
                                    }))

                                    setErrorValues((prev) => ({
                                        ...prev,
                                        sentSchedulerScreeningInterviewTeamSelection:
                                            newValue === null
                                                ? 'Screening interview team selection is required'
                                                : '',
                                    }))
                                }}
                                value={formValues.screeningInterviewTeam}
                                options={
                                    screeningInterviewTeams.screeningInterviewTeams
                                }
                            />
                        )}
                    </Stack>
                </Stack>
                <Divider />
                <Stack gap={2}>
                    <Subtitle variant="subtitle1">Profile Details</Subtitle>
                    <Stack gap={3}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={formValues.firstName}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    firstName: e.target.value,
                                }))
                                if (!e.target.value || e.target.value === '') {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        firstName: 'First name is required',
                                    }))
                                } else {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        firstName: '',
                                    }))
                                }
                            }}
                            error={!!errorValues.firstName}
                            helperText={errorValues.firstName}
                        />
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={formValues.lastName}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    lastName: e.target.value,
                                }))
                                if (!e.target.value || e.target.value === '') {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        lastName: 'Last name is required',
                                    }))
                                } else {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        lastName: '',
                                    }))
                                }
                            }}
                            error={!!errorValues.lastName}
                            helperText={errorValues.lastName}
                        />
                        <TextField
                            fullWidth
                            label="Preferred Name"
                            value={formValues.nickname}
                            onChange={(e) =>
                                setFormValues((prev) => ({
                                    ...prev,
                                    nickname: e.target.value,
                                }))
                            }
                        />
                        <TextField
                            fullWidth
                            label="LinkedIn URI"
                            value={formValues.linkedinUrl}
                            error={!!errorValues.linkedinUrl}
                            helperText={errorValues.linkedinUrl}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    linkedinUrl: e.target.value,
                                }))
                                if (
                                    e.target.value?.length === 0 ||
                                    isValidLinkedInUrl(e.target.value)
                                ) {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        linkedinUrl: '',
                                    }))
                                } else {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        linkedinUrl: 'Invalid LinkedIn URL',
                                    }))
                                }
                            }}
                        />
                        <NonFormikPhoneInput
                            label="Personal Cell Phone"
                            handleInputChange={(newValue) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    phone: newValue,
                                }))
                                if (
                                    newValue &&
                                    parsePhoneNumber(newValue)?.isValid()
                                ) {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        phone: '',
                                    }))
                                } else {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        phone: 'Invalid phone number',
                                    }))
                                }
                            }}
                            value={formValues.phone ?? ''}
                            error={!!errorValues.phone}
                            helperText={errorValues.phone}
                        />
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                fullWidth
                                label="Personal Email"
                                value={formValues.email}
                                onChange={(e) => {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        email: e.target.value,
                                    }))
                                    if (
                                        !e.target.value ||
                                        e.target.value === ''
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            email: 'Email is required',
                                        }))
                                    } else if (
                                        !Yup.string()
                                            .email()
                                            .isValidSync(e.target.value)
                                    ) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            email: 'Invalid email',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            email: '',
                                        }))
                                    }
                                }}
                                error={!!errorValues.email}
                                helperText={errorValues.email}
                            />
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                fullWidth
                                label="Work Email"
                                value={formValues.workEmail}
                                disabled={true}
                            />
                            {
                                <FormControlLabel
                                    control={<Checkbox />}
                                    label="Prefers Work Email"
                                    checked={formValues.prefersWorkEmail}
                                    sx={{ marginLeft: 0 }}
                                    onChange={() =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            prefersWorkEmail:
                                                !prev.prefersWorkEmail,
                                        }))
                                    }
                                />
                            }
                        </Box>
                    </Stack>
                </Stack>
                <DrawerCancelSave
                    isSaveDisabled={
                        !unsavedChangesExist ||
                        Object.values(errorValues).some((error) => error !== '')
                    }
                    isEditMode={true}
                    handleCancel={handleCancel}
                    handleSave={handleSave}
                />
            </Stack>
            {/* Edit profile image dialog */}
            <Dialog
                open={dialogOpen}
                onClose={!imageLoading ? closeDialog : undefined}
            >
                {imageLoading ? (
                    <StyledDialogContent>
                        <CircularProgress />
                        <Typography variant="h6" component="h1">
                            Uploading new profile picture
                        </Typography>
                    </StyledDialogContent>
                ) : (
                    <StyledDialogContent>
                        <Avatar src={image} sx={{ width: 180, height: 180 }} />
                        <DialogButtonContainer>
                            <Button
                                variant="outlined"
                                onClick={closeDialog}
                                type="button"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleUpload}>Upload Image</Button>
                        </DialogButtonContainer>
                    </StyledDialogContent>
                )}
            </Dialog>
        </DrawerComponent>
    )
}

export default EditProfile
